import React from "react";
import { motion } from "framer-motion";

// A subtle background that mutates by era. Uses only black + yellow.
// Renders a fixed layer with radial/grid patterns behind the game.
const eraStyles = {
  hand: { pattern: "dots-sparse", accentOpacity: 0.03 },
  log: { pattern: "dots-sparse", accentOpacity: 0.04 },
  wood_wheel: { pattern: "dots-sparse", accentOpacity: 0.05 },
  spoked_wheel: { pattern: "grid", accentOpacity: 0.05 },
  war_chariot: { pattern: "grid", accentOpacity: 0.06 },
  roman_chariot: { pattern: "grid", accentOpacity: 0.07 },
  medieval_wagon: { pattern: "diagonal", accentOpacity: 0.06 },
  industrial: { pattern: "diagonal", accentOpacity: 0.08 },
  locomotive: { pattern: "diagonal", accentOpacity: 0.09 },
  bicycle: { pattern: "concentric", accentOpacity: 0.08 },
  automobile: { pattern: "concentric", accentOpacity: 0.09 },
  f1: { pattern: "concentric", accentOpacity: 0.1 },
  space: { pattern: "stars", accentOpacity: 0.14 },
  community: { pattern: "stars", accentOpacity: 0.2 },
};

const PatternDots = ({ opacity }) => (
  <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <pattern id="dots" width="40" height="40" patternUnits="userSpaceOnUse">
        <circle cx="1" cy="1" r="1" fill="#FFD400" opacity={opacity} />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#dots)" />
  </svg>
);

const PatternGrid = ({ opacity }) => (
  <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <pattern id="grid" width="60" height="60" patternUnits="userSpaceOnUse">
        <path d="M60 0 L0 0 0 60" fill="none" stroke="#FFD400" strokeWidth="0.5" opacity={opacity} />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#grid)" />
  </svg>
);

const PatternDiagonal = ({ opacity }) => (
  <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <pattern id="diag" width="24" height="24" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
        <line x1="0" y1="0" x2="0" y2="24" stroke="#FFD400" strokeWidth="1" opacity={opacity} />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#diag)" />
  </svg>
);

const PatternConcentric = ({ opacity }) => (
  <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice">
    {Array.from({ length: 16 }).map((_, i) => (
      <circle
        key={i}
        cx="50%"
        cy="50%"
        r={80 + i * 90}
        fill="none"
        stroke="#FFD400"
        strokeWidth="1"
        opacity={opacity}
      />
    ))}
  </svg>
);

const PatternStars = ({ opacity }) => {
  const stars = React.useMemo(
    () =>
      Array.from({ length: 80 }).map((_, i) => ({
        cx: Math.random() * 100,
        cy: Math.random() * 100,
        r: Math.random() * 1.4 + 0.3,
        o: Math.random() * opacity + 0.05,
        key: i,
      })),
    [opacity],
  );
  return (
    <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" viewBox="0 0 100 100">
      {stars.map((s) => (
        <circle key={s.key} cx={s.cx} cy={s.cy} r={s.r} fill="#FFD400" opacity={s.o} />
      ))}
    </svg>
  );
};

const patternMap = {
  "dots-sparse": PatternDots,
  grid: PatternGrid,
  diagonal: PatternDiagonal,
  concentric: PatternConcentric,
  stars: PatternStars,
};

export const Background = ({ eraKey = "hand" }) => {
  const cfg = eraStyles[eraKey] || eraStyles.hand;
  const Pattern = patternMap[cfg.pattern] || PatternDots;
  return (
    <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none" data-testid="era-background">
      <div className="absolute inset-0" style={{ backgroundColor: "#0b0b0b" }} />
      <motion.div
        key={eraKey}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2 }}
        className="absolute inset-0"
      >
        <Pattern opacity={cfg.accentOpacity} />
      </motion.div>
      {/* Vignette */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(0,0,0,0) 40%, rgba(0,0,0,0.75) 100%)",
        }}
      />
    </div>
  );
};

export default Background;
